import { Info } from "lucide-react";
import { useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ExtractionHint } from "@noa/api-core";
import { compactFormItemStyle } from "~/utils/schema-admin";
import { toValueLabel } from "~/utils/form-utils";
import { type ExtractionSource, getFieldsToKeepOnSourceChange, inferExtractionSource } from "~/utils/pattern-utils";
import { DefaultValueEditor } from "./default-value-editor";
import { DualPatternTester } from "./dual-pattern-tester";
import { KeyValueEditor } from "./key-value-editor";
import { PatternEditor } from "./pattern-editor";
import { PromptEditor } from "./prompt-editor";
import { CheckboxGroup, FormItem, Input, Segmented, Select, Text } from "~/components/ui";

const LLM_OUTPUT_TYPE_OPTIONS = ["string", "int", "bool", "enum", "list[string]"].map(toValueLabel);

interface ExtractionSourceFormProps {
  fieldIndex: number;
  onSourceChange?: (source: ExtractionSource) => void;
}

export function ExtractionSourceForm({ fieldIndex, onSourceChange }: ExtractionSourceFormProps) {
  const { t } = useTranslation("admin");
  const { control, setValue, getValues } = useFormContext();

  const extraction = useWatch({ control, name: `fields.${fieldIndex}.extraction` });
  const fieldType = useWatch({ control, name: `fields.${fieldIndex}.type` }) || "string";

  // The source is inferred once from the stored extraction, then driven by the
  // user only. Re-inferring on every extraction change made the selector bounce
  // back (e.g. path -> llm reverted to static) and destroyed the cleared keys.
  const [selectedSource, setSelectedSource] = useState<ExtractionSource>(() =>
    inferExtractionSource(getValues(`fields.${fieldIndex}.extraction`))
  );
  const [prevFieldIndex, setPrevFieldIndex] = useState(fieldIndex);
  if (fieldIndex !== prevFieldIndex) {
    setPrevFieldIndex(fieldIndex);
    setSelectedSource(inferExtractionSource(getValues(`fields.${fieldIndex}.extraction`)));
  }

  const handleSourceChange = (newSource: ExtractionSource) => {
    const currentExtraction = getValues(`fields.${fieldIndex}.extraction`) as ExtractionHint | undefined;
    const fieldsToKeep = getFieldsToKeepOnSourceChange(newSource, currentExtraction);
    setValue(`fields.${fieldIndex}.extraction`, fieldsToKeep);
    if (newSource === "llm" && !getValues(`fields.${fieldIndex}.validation_phase`)) {
      // LLM extraction runs after parsing; pre_parse would silently skip it
      setValue(`fields.${fieldIndex}.validation_phase`, "post_parse");
    }
    setSelectedSource(newSource);
    onSourceChange?.(newSource);
  };

  const sourceOptions = [
    { label: t("schemaAdmin.extraction.sourceStatic"), value: "static" },
    { label: t("schemaAdmin.extraction.sourceMetadata"), value: "metadata" },
    { label: t("schemaAdmin.extraction.sourcePath"), value: "path" },
    { label: t("schemaAdmin.extraction.sourceComputed"), value: "computed" },
    { label: t("schemaAdmin.extraction.sourceLlm"), value: "llm" },
  ];

  return (
    <div className="space-y-4">
      <FormItem label={t("schemaAdmin.extraction.source")} className={compactFormItemStyle} group>
        <Segmented
          options={sourceOptions}
          value={selectedSource}
          onChange={(v) => handleSourceChange(v as ExtractionSource)}
          block
        />
      </FormItem>

      <Text tone="muted" className="text-xs block">
        {t(`schemaAdmin.extraction.${selectedSource}Hint`)}
      </Text>

      {/* Static Source */}
      {selectedSource === "static" && (
        <FormItem label={t("schemaAdmin.field.defaultValue")} className={compactFormItemStyle}>
          <Controller
            name={`fields.${fieldIndex}.extraction.default_value`}
            control={control}
            render={({ field }) => (
              <DefaultValueEditor value={field.value} onChange={field.onChange} fieldType={fieldType} />
            )}
          />
        </FormItem>
      )}

      {/* Metadata Source */}
      {selectedSource === "metadata" && (
        <>
          <Controller
            name={`fields.${fieldIndex}.extraction.from_metadata_key`}
            control={control}
            render={({ field, fieldState }) => (
              <FormItem
                label={t("schemaAdmin.field.metadataKey")}
                className={compactFormItemStyle}
                required
                validateStatus={fieldState.error ? "error" : ""}
                help={fieldState.error ? t("common:formValidations.required") : undefined}
              >
                <Input {...field} value={field.value ?? ""} placeholder="metadata_key" className="font-mono" />
              </FormItem>
            )}
          />
          <FormItem label={`${t("schemaAdmin.field.defaultValue")} (fallback)`} className={compactFormItemStyle}>
            <Controller
              name={`fields.${fieldIndex}.extraction.default_value`}
              control={control}
              render={({ field }) => (
                <DefaultValueEditor value={field.value} onChange={field.onChange} fieldType={fieldType} />
              )}
            />
          </FormItem>
        </>
      )}

      {/* Path Source */}
      {selectedSource === "path" && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <FormItem label={`${t("schemaAdmin.field.pathPattern")} (regex)`} className={compactFormItemStyle} group>
              <Controller
                name={`fields.${fieldIndex}.extraction.from_path_pattern`}
                control={control}
                render={({ field }) => (
                  <PatternEditor value={field.value} onChange={field.onChange} regexFlags={extraction?.regex_flags} />
                )}
              />
            </FormItem>
            <FormItem
              label={`${t("schemaAdmin.field.filenamePattern")} (regex)`}
              className={compactFormItemStyle}
              group
            >
              <Controller
                name={`fields.${fieldIndex}.extraction.from_filename_pattern`}
                control={control}
                render={({ field }) => (
                  <PatternEditor
                    value={field.value}
                    onChange={field.onChange}
                    mode="filename"
                    regexFlags={extraction?.regex_flags}
                  />
                )}
              />
            </FormItem>
          </div>

          <DualPatternTester
            pathPattern={extraction?.from_path_pattern}
            filenamePattern={extraction?.from_filename_pattern}
            regexFlags={extraction?.regex_flags}
          />

          <FormItem label={t("schemaAdmin.field.regexFlags")} className={compactFormItemStyle} group>
            <Controller
              name={`fields.${fieldIndex}.extraction.regex_flags`}
              control={control}
              render={({ field }) => (
                <CheckboxGroup
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={["IGNORECASE", "MULTILINE", "DOTALL"].map(toValueLabel)}
                />
              )}
            />
          </FormItem>

          <FormItem label={t("schemaAdmin.field.pathPrefix")} className={compactFormItemStyle} group>
            <Controller
              name={`fields.${fieldIndex}.extraction.from_path_prefix`}
              control={control}
              render={({ field }) => (
                <KeyValueEditor
                  value={field.value}
                  onChange={field.onChange}
                  keyPlaceholder="/0000-"
                  valuePlaceholder="SITE_ID"
                  addLabel={t("schemaAdmin.addMapping")}
                />
              )}
            />
          </FormItem>

          <FormItem label={t("schemaAdmin.field.valueMap")} className={compactFormItemStyle} group>
            <Controller
              name={`fields.${fieldIndex}.extraction.value_map`}
              control={control}
              render={({ field }) => (
                <KeyValueEditor
                  value={field.value}
                  onChange={field.onChange}
                  keyPlaceholder="E"
                  valuePlaceholder="Électrique"
                  addLabel={t("schemaAdmin.addMapping")}
                />
              )}
            />
          </FormItem>

          <FormItem label={`${t("schemaAdmin.field.defaultValue")} (fallback)`} className={compactFormItemStyle}>
            <Controller
              name={`fields.${fieldIndex}.extraction.default_value`}
              control={control}
              render={({ field }) => (
                <DefaultValueEditor value={field.value} onChange={field.onChange} fieldType={fieldType} />
              )}
            />
          </FormItem>
        </>
      )}

      {/* Computed Source */}
      {selectedSource === "computed" && (
        <div className="grid grid-cols-2 gap-4">
          <Controller
            name={`fields.${fieldIndex}.extraction.from_field`}
            control={control}
            render={({ field, fieldState }) => (
              <FormItem
                label={t("schemaAdmin.field.fromField")}
                className={compactFormItemStyle}
                required
                validateStatus={fieldState.error ? "error" : ""}
                help={fieldState.error ? t("common:formValidations.required") : undefined}
              >
                <Input {...field} value={field.value ?? ""} placeholder="asset_id" className="font-mono" />
              </FormItem>
            )}
          />
          <FormItem label={t("schemaAdmin.field.transform")} className={compactFormItemStyle} required>
            <Controller
              name={`fields.${fieldIndex}.extraction.transform`}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? null}
                  placeholder={t("schemaAdmin.placeholders.selectTransform")}
                  options={["asset_code_variants", "asset_code_or_none"].map(toValueLabel)}
                  className="w-full"
                />
              )}
            />
          </FormItem>
        </div>
      )}

      {/* LLM Source */}
      {selectedSource === "llm" && (
        <>
          <Text tone="muted" className="text-xs block mb-3">
            <Info size={14} className="mr-1" />
            {t("schemaAdmin.extraction.llmValidationNote")}
          </Text>

          <Controller
            name={`fields.${fieldIndex}.extraction.from_llm.prompt_hint`}
            control={control}
            render={({ field, fieldState }) => (
              <FormItem
                label={t("schemaAdmin.field.prompt")}
                className={compactFormItemStyle}
                required
                validateStatus={fieldState.error ? "error" : ""}
                help={fieldState.error ? t("common:formValidations.required") : undefined}
              >
                <PromptEditor value={field.value} onChange={field.onChange} />
              </FormItem>
            )}
          />

          <FormItem label={t("schemaAdmin.field.llmOutputType")} className={compactFormItemStyle}>
            <Controller
              name={`fields.${fieldIndex}.extraction.from_llm.output_type`}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? null}
                  options={LLM_OUTPUT_TYPE_OPTIONS}
                  allowClear
                  className="w-full max-w-xs"
                />
              )}
            />
          </FormItem>
        </>
      )}
    </div>
  );
}
